<template>
	<div>
		<!-- The normal scheduled opening status. If today is mentioned in the
        exceptional opening hours text, don't render this as it may conflict
        with the exceptional opening hours. -->
		<template v-if="!isExceptionHoursDay">
			<p v-if="isStoreOpen && !isStoreClosingSoon" class="text-green-500">
				{{ t("is-open") }} {{ todaysOpenStatusString }}
			</p>

			<p v-if="isStoreOpen && isStoreClosingSoon" class="text-[#E2B301]">
				{{ t("closing-soon", {closingTimeToday: todaysOpeningTime.closing}) }}
			</p>

			<p v-if="isStoreClosedForSure" class="text-red-500">
				<span v-if="isStoreOpeningSoon" class="text-[#E2B301]">
					{{
						t("opening-soon", {todaysOpeningTime: todaysOpeningTime.opening})
					}}
				</span>

				<span v-else-if="isStoreOpeningToday">
					{{
						t("opening-today", {todaysOpeningTime: todaysOpeningTime.opening})
					}}
				</span>

				<span v-else-if="nextAvailableOpeningDay.day">
					{{
						t("opening-other-day", {
							day: nextAvailableOpeningDay.day,
							hour: nextAvailableOpeningDay.hour,
						})
					}}
				</span>

				<span v-else>
					{{ t("closed") }}
				</span>
			</p>
		</template>

		<sb-rich-text
			v-if="haveExceptionsText"
			:document="exceptionsText"
			class="prose prose-sm mt-2 wrap-break-word lg:prose-lg xl:prose-xl"
		/>

		<p
			v-if="!isHoursTableProvided && !haveExceptionsText"
			class="text-gray-300"
		>
			{{ t("no-opening-hours") }}
		</p>
	</div>
</template>

<script setup lang="ts">
import type {IHoursTable} from "@pkg/shared/types";
import type {ISbStoryData} from "storyblok-js-client";

import isFilledRichText from "@pkg/shared/utils/is-filled-rich-text";

type OpeningHours =
	| {
			opening: number;
			closing: number;
	  }
	| {
			opening: null;
			closing: null;
	  };

const properties = defineProps<{
	outlet: ISbStoryData;
}>();

const {t} = useI18n();

const exceptionsText = computed(() => properties.outlet.content.hours);
const haveExceptionsText = computed(() =>
	isFilledRichText(exceptionsText.value),
);

const time = new Date();
const currentDateString = `${time.getDate()}.${time.getMonth() + 1}.${time.getFullYear()}`;

const isExceptionHoursDay = computed(() => {
	// We could render rich text and then maybe transform that into lines (which is harder than expected, to get the text with appropriate line breaks you need to create a HTML node, insert it into the DOM and then get its innerText), but here just lazily searching the entire JSON value of the text is enough.
	const exceptionsString =
		typeof exceptionsText.value === "object"
			? JSON.stringify(exceptionsText.value)
			: exceptionsText.value;
	return exceptionsString.includes(currentDateString);
});

const currentDay = new Intl.DateTimeFormat(t("date-locale"), {
	weekday: "long",
}).format();
const currentDayUpperCase =
	currentDay.charAt(0).toUpperCase() + currentDay.slice(1);

const dayHoursTable = computed(
	() => properties.outlet.content.hours_table ?? null,
);

/**
 * Finds the row / open status in the hours table that matches the given day.
 * @param data - The hours table data.
 * @param day - The day to search for.
 * @returns - The opening hours string for the given day (empty if no match found).
 */
function findOpenStatusByDay(data: IHoursTable, day: string) {
	// Check if data is defined and if tbody exists in data
	if (data && Array.isArray(data.tbody)) {
		// Iterate through the tbody array to find the matching day
		for (const row of data.tbody) {
			const [dayCell, hoursCell] = Array.isArray(row.body) ? row.body : [];

			// Check if the row has a body and the first item empty
			if (!dayCell || !hoursCell || dayCell.value !== day) {
				continue;
			}

			// Day matches, return the cell with opening hours
			try {
				return hoursCell.value.trim();
			} catch {
				return hoursCell.value;
			}
		}
	}
	return "";
}

const todaysOpenStatusString = computed(() =>
	findOpenStatusByDay(dayHoursTable.value, currentDayUpperCase),
);

const isHoursTableProvided = computed(() => {
	if (dayHoursTable.value) {
		return dayHoursTable.value.tbody.length > 2;
	}
	return false;
});

const currentHour = new Date().getHours();

/** Regex for - and – characters. */
const openingTimeSeparatorRegex = /\s*[–-]\s*/;

/** Opening hours are written as "10", "10.00" or "10:00" - we only care about the hour. Returns NaN when there's no hour to read. */
const toHour = (part: string | undefined) => {
	const hour = part?.match(/^\d+/)?.[0];

	return hour === undefined ? NaN : Number(hour);
};

/** Get opening and closing hours from a string that matches the hours table's opening hours format for a weekday. If no match, the store probably isn't open at the given point, but it might also just be lacking a proper hours table and still be open. */
const getOpeningHoursFromString = (hoursString: string): OpeningHours => {
	if (hoursString === "") {
		return {opening: null, closing: null};
	}

	const split = hoursString.split(openingTimeSeparatorRegex);

	// String didn't match opening time format
	if (split.length === 1) {
		return {opening: null, closing: null};
	}

	const [openingPart, closingPart] = split;

	const opening = toHour(openingPart);
	const closing = toHour(closingPart);

	if (Number.isNaN(opening) || Number.isNaN(closing)) {
		return {opening: null, closing: null};
	}

	return {opening, closing};
};

const todaysOpeningTime = computed(() =>
	getOpeningHoursFromString(todaysOpenStatusString.value),
);

const isStoreOpeningToday = computed(() => {
	if (todaysOpeningTime.value.opening === null) {
		return false;
	}
	return currentHour < todaysOpeningTime.value.closing;
});

/**
 * Computes whether the store is opening soon.
 * @returns {boolean} True if the store is opening in the next two hours, false otherwise.
 */
const isStoreOpeningSoon = computed(() => {
	if (todaysOpeningTime.value.opening === null) {
		return false;
	}

	const openingToday = todaysOpeningTime.value.opening;
	// Check if the store is opening in the next two hours
	return openingToday <= currentHour + 2 && openingToday > currentHour;
});

/**
 * Computes whether the store is closing soon.
 * @returns {boolean} True if the store is closing in the next hour, false otherwise.
 */
const isStoreClosingSoon = computed(() => {
	if (todaysOpeningTime.value.closing === null) {
		return false;
	}

	const closingTimeToday = todaysOpeningTime.value.closing;
	// Check if the store is closing in the next hour
	return closingTimeToday <= currentHour + 1 && closingTimeToday > currentHour;
});

/**
 * Computes whether the store is currently open based on the current hour and the opening hours for today.
 * @returns {boolean} True if the store is open, false otherwise.
 */
const isStoreOpen = computed(() => {
	if (todaysOpeningTime.value.opening === null) {
		return false;
	}

	const openingToday = todaysOpeningTime.value.opening;
	const closingTimeToday = todaysOpeningTime.value.closing;
	// Check if the current hour is between the opening and closing times
	return currentHour >= openingToday && currentHour < closingTimeToday;
});

/**
 * Whether the store is known to be closed currently closed for certain. If opening hours aren't provided, the store might still be open and this won't be true.
 */
const isStoreClosedForSure = computed(() => {
	if (!isHoursTableProvided.value) {
		return false;
	}

	const today = todaysOpeningTime.value;
	// If no valid opening hours string is provided for today, we assume the store should be shown as closed.
	if (today.opening === null) {
		return true;
	}

	// Check if the current hour is outside of the opening hours.
	return currentHour < today.opening || currentHour >= today.closing;
});

const days = [
	t("days.monday"),
	t("days.tuesday"),
	t("days.wednesday"),
	t("days.thursday"),
	t("days.friday"),
	t("days.saturday"),
	t("days.sunday"),
];

/** Use daysInessive array for days to get inessive ("in") days returned as value in nextOpeningDay.
 * We also need to use plain days array for the findOpenStatusByDay function to get the correct opening hours for the day.
 */
const daysInessive = [
	t("days.monday", 2),
	t("days.tuesday", 2),
	t("days.wednesday", 2),
	t("days.thursday", 2),
	t("days.friday", 2),
	t("days.saturday", 2),
	t("days.sunday", 2),
];

/**
 * Computes the next available opening day and opening hours based on the current day and outlet properties.
 *
 * @returns {Object} An object containing the next opening day and opening hours.
 *                   - day: The next available opening day.
 *                   - hours: The opening hours for the next available opening day.
 */
const nextAvailableOpeningDay = computed(() => {
	const currentDayIndex = (days.indexOf(currentDayUpperCase) + 1) % days.length;

	if (properties.outlet !== undefined) {
		// Try looping through every weekday in order (from this day onward, wrapping around) in the table to see if there's an upcoming open day.
		for (let index = 0; index < days.length; index += 1) {
			const nextDayIndex = (currentDayIndex + index) % days.length;
			const openingStatus = findOpenStatusByDay(
				dayHoursTable.value,
				days[nextDayIndex] ?? "",
			);
			const openingTime = getOpeningHoursFromString(openingStatus);
			// We assume that the store is closed if no concrete opening hours are provided.
			if (openingTime.opening) {
				return {
					day: daysInessive[nextDayIndex],
					hour: openingTime.opening,
				};
			}
		}
	}

	return {day: null, hour: null};
});
</script>

<i18n lang="json">
{
	"et": {
		"days": {
			"monday": "Esmaspäev | Esmaspäeval",
			"tuesday": "Teisipäev | Teisipäeval",
			"wednesday": "Kolmapäev | Kolmapäeval",
			"thursday": "Neljapäev | Neljapäeval",
			"friday": "Reede | Reedel",
			"saturday": "Laupäev | Laupäeval",
			"sunday": "Pühapäev | Pühapäeval"
		},
		"date-locale": "et-ET",
		"closed": "Suletud",
		"is-open": "Avatud",
		"no-opening-hours": "Lahtiolekuaegu pole määratud",
		"closing-soon": "Sulgemine varsti, kell { closingTimeToday }",
		"opening-other-day": "Suletud, avatakse { day } kell { hour }",
		"opening-today": "Suletud, avatakse täna kell { todaysOpeningTime }",
		"opening-soon": "Avatakse varsti, kell { todaysOpeningTime }",
		"exceptions": "Erandid"
	},
	"fi": {
		"date-locale": "fi-FI",
		"closed": "Suljettu",
		"is-open": "Avoinna",
		"no-opening-hours": "Aukioloaikoja ei saatavilla",
		"closing-soon": "Suljetaan pian, klo { closingTimeToday }",
		"opening-other-day": "Suljettu, avataan { day } klo { hour }",
		"opening-today": "Suljettu, avataan tänään klo { todaysOpeningTime }",
		"opening-soon": "Avataan pian, klo { todaysOpeningTime }",
		"days": {
			"monday": "Maanantai | Maanantaina",
			"tuesday": "Tiistai | Tiistaina",
			"wednesday": "Keskiviikko | Keskiviikkona",
			"thursday": "Torstai | Torstaina",
			"friday": "Perjantai | Perjantaina",
			"saturday": "Lauantai | Lauantaina",
			"sunday": "Sunnuntai | Sunnuntaina"
		},
		"exceptions": "Poikkeukset"
	}
}
</i18n>
